import { mergeClasses, TreeItem, TreeItemLayout } from '@fluentui/react-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountAmountStyles } from '../../../hooks/useAccountAmountStyles';
import { useDrillDownStyles } from '../../../hooks/useDrillDownStyles';
import { formatNumberForDisplay, parseNumericValue } from '../../../utils/NumberUtils';
import { JournalEntryDialogButton } from '../JournalEntryDialogButton';
import { AccountAmountWhitePaperRow } from '../ReportDrillDownUtils';
import { IAccountAmountBookEntryRowProps } from './types';

function stopClickPropagation(e: React.MouseEvent): void {
    e.stopPropagation();
}

function reformatNumericText(value: string, numberFormatter: (v: number) => string): string {
    return numberFormatter(parseNumericValue(value, true));
}

export const AccountAmountBookEntryRow: FC<IAccountAmountBookEntryRowProps> = (props: IAccountAmountBookEntryRowProps) => {
    const row: AccountAmountWhitePaperRow = props.row;
    const styles = useAccountAmountStyles();
    const drillDownStyles = useDrillDownStyles();
    const { t } = useTranslation();

    const entityLabelId = `${row.uiKey}-entity-label`;
    const entityValueId = `${row.uiKey}-entity-value`;
    const caseLabelId = `${row.uiKey}-case-label`;
    const caseValueId = `${row.uiKey}-case-value`;
    const jurisdictionLabelId = `${row.uiKey}-jurisdiction-label`;
    const jurisdictionValueId = `${row.uiKey}-jurisdiction-value`;
    const yearLabelId = `${row.uiKey}-year-label`;
    const yearValueId = `${row.uiKey}-year-value`;

    return (
        <TreeItem itemType='leaf' key={row.uiKey} value={row.uiKey!} onClick={stopClickPropagation}>
            <TreeItemLayout
                main={{ className: mergeClasses(styles.flexFullWidth, styles.treeItemLayoutMain, props.containerClassName) }}
                className={mergeClasses('book-entry-row', styles.alignItemsStart, styles.bookEntryRow, props.className)}
            >
                <div
                    role='gridcell'
                    aria-labelledby={`${entityLabelId} ${entityValueId}`}
                    className={mergeClasses(styles.alignContentCenter, styles.entityColumn, styles.fontSize)}
                >
                    <div className={mergeClasses(styles.cellText)}>
                        <span id={entityLabelId} className={styles.fontWeightSemibold}>
                            {t('entity')}:
                        </span>
                        <div id={entityValueId}>{row.entity}</div>
                    </div>
                </div>
                <div
                    role='gridcell'
                    aria-labelledby={`${caseLabelId} ${caseValueId}`}
                    className={mergeClasses(styles.alignContentCenter, styles.caseColumn, styles.fontSize)}
                >
                    <div className={mergeClasses(styles.cellText)}>
                        <span id={caseLabelId} className={styles.fontWeightSemibold}>
                            {t('case')}:
                        </span>
                        <div id={caseValueId}>{row.case}</div>
                    </div>
                </div>
                <div
                    role='gridcell'
                    aria-labelledby={`${jurisdictionLabelId} ${jurisdictionValueId}`}
                    className={mergeClasses(styles.alignContentCenter, styles.jurisdictionColumn, styles.fontSize)}
                >
                    <div className={mergeClasses(styles.cellText)}>
                        <span id={jurisdictionLabelId} className={styles.fontWeightSemibold}>
                            {t('jurisdiction')}:
                        </span>
                        <div id={jurisdictionValueId}>{row.jurisdiction}</div>
                    </div>
                </div>
                <div
                    role='gridcell'
                    aria-labelledby={`${yearLabelId} ${yearValueId}`}
                    className={mergeClasses(styles.alignContentCenter, styles.periodColumn, styles.fontSize)}
                >
                    <div className={mergeClasses(styles.cellText)}>
                        <span id={yearLabelId} className={styles.fontWeightSemibold}>
                            {t('year')}:
                        </span>
                        <div id={yearValueId}>{row.year}</div>
                    </div>
                </div>
                <div
                    role='gridcell'
                    className={mergeClasses(styles.alignContentCenter, styles.lastModifiedByColumn, styles.fontSize)}
                ></div>
                <div
                    role='gridcell'
                    className={mergeClasses(styles.alignContentCenter, styles.journalEntryButtonHeader, styles.disabled, styles.fontSize)}
                >
                    {props.journalEntryButtonDisplayState !== 'invisible' && (
                        <JournalEntryDialogButton
                            className={drillDownStyles.iconOnlyButton}
                            disabled={props.journalEntryButtonDisplayState === 'disabled'}
                            onButtonClick={props.onJournalEntryClick!}
                        />
                    )}
                </div>
                <div
                    role='gridcell'
                    className={mergeClasses(styles.alignContentCenter, styles.enterAmountsButtonHeader, styles.disabled, styles.fontSize)}
                ></div>
                <div
                    role='gridcell'
                    className={mergeClasses(styles.reportAmountColumn, styles.alignContentCenter, styles.disabled, styles.fontSize)}
                    data-automation-key='amount'
                >
                    {reformatNumericText(row.amount!, formatNumberForDisplay)}
                </div>
            </TreeItemLayout>
        </TreeItem>
    );
};
